import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../state-machine/sendBoardEvent";
import { useContext } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../state-machine/reviewBoardFlowLoader";
import {
  PickAndPlace,
  updateBoardNicknameInStore,
} from "../../../../../reducers/uploadedBoard";
import { usePickAndPlaceData } from "../pickAndPlaceFiles";
import { useAppDispatch } from "../../../store";
import useGetInventoryItems from "../../../inventory/getInventoryItems";
import { InventoryItemType } from "../../../inventory/useInventoryItems";

export interface StencilUpdateRequest {
  stencil_ids: string[];
}

export function useRemoveBoardStencilAssociation() {
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const { refreshInventoryItems } = useGetInventoryItems(
    InventoryItemType.STENCIL,
  );
  const removeStencilFromBoard = (
    boardId: string,
    stencilUpdateRequest: StencilUpdateRequest,
  ) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.REMOVE_STENCIL,
      update: stencilUpdateRequest,
    }).then(() => {
      refreshInventoryItems();
    });
  };

  return { removeStencilFromBoard, removingStencilAssociation: loading };
}

export function useAddBoardStencilAssociation() {
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const { refreshInventoryItems } = useGetInventoryItems(
    InventoryItemType.STENCIL,
  );
  const addStencilToBoard = (
    boardId: string,
    stencilUpdateRequest: StencilUpdateRequest,
  ) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.ADD_STENCIL,
      update: stencilUpdateRequest,
    }).then(() => {
      refreshInventoryItems();
    });
  };

  return { addStencilToBoard, addingStencilAssociation: loading };
}

export interface PanelUpdateRequest {
  panel_ids: string[];
}

export function useRemoveBoardPanelAssociation() {
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const { refreshInventoryItems } = useGetInventoryItems(
    InventoryItemType.PANELS,
  );
  const removePanelFromBoard = (
    boardId: string,
    panelUpdateRequest: PanelUpdateRequest,
  ) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.REMOVE_PANELS,
      update: panelUpdateRequest,
    }).then(() => {
      refreshInventoryItems();
    });
  };

  return { removePanelFromBoard, removingPanelAssociation: loading };
}

export function useAddBoardPanelAssociation() {
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const { refreshInventoryItems } = useGetInventoryItems(
    InventoryItemType.PANELS,
  );
  const addPanelToBoard = (
    boardId: string,
    panelUpdateRequest: PanelUpdateRequest,
  ) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.ADD_PANELS,
      update: panelUpdateRequest,
    }).then(() => {
      refreshInventoryItems();
    });
  };

  return { addPanelToBoard, addingPanelAssociation: loading };
}

export function useUpdatePickAndPlaceData() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const { refreshPickAndPlaceData } = usePickAndPlaceData(boardId);
  const { sendBoardEvent, loading: updatingPickAndPlaceData } =
    useSendBoardEvent();

  const updatePickAndPlaceData = (update: { pick_and_place: PickAndPlace }) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.SHOULD_UPDATE_PNP_TRANSFORMATIONS,
      update: update,
    }).then(() => {
      refreshPickAndPlaceData();
    });
  };
  return {
    updatePickAndPlaceData,
    updatingPickAndPlaceData,
  };
}

export function useUpdateBoardNickname() {
  const { sendBoardEvent, loading } = useSendBoardEvent();
  const dispatch = useAppDispatch();
  const updateInternalNickname = (boardId: string, nickname: string) => {
    void sendBoardEvent(boardId, {
      machine_group: MachineGroup.BOARD,
      model_id: boardId,
      event: BoardTransitionEvent.UPDATE_NICKNAME,
      update: { nickname },
    });
    dispatch(updateBoardNicknameInStore({ boardId, nickname }));
  };
  return {
    updateInternalNickname,
    updatingNickname: loading,
  };
}
